import { FiBook } from 'react-icons/fi';

export const awlSublist10Data = {
  courseId: 'awl-sublist-10',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 10',
  icon: FiBook,
  overview: {
    impact: 'Master the least frequent AWL glue words that still unlock dense Reading and elegant concessions',
    focus: 'albeit, persist, undergo — plus compile, intrinsic, nonetheless, whereby',
    uses: 'Advanced Reading clauses and Writing Task 2 concessions, processes, and formal contrast',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word meanings and usage',
      content: `<p>Sublist 10 is the smallest and rarest AWL band, but it contains some of the most useful academic “glue”: <em>albeit</em>, <em>nonetheless</em>, <em>notwithstanding</em>, <em>likewise</em>, <em>whereby</em>. These words do not name topics; they show how clauses relate. That is why they appear in Band 8 model essays and in the hardest Reading sentences.</p>
<p>The full Sublist 10 headword set in this course is: adjacent, albeit, assemble, collapse, colleague, compile, conceive, convince, depress, encounter, enormous, forthcoming, incline, integrity, intrinsic, invoke, levy, likewise, nonetheless, notwithstanding, odd, ongoing, panel, persist, pose, reluctance, so-called, straightforward, undergo, whereby.</p>
<p>The three focus items are workhorses. <strong>Albeit</strong> means “although” inside a phrase: “useful, albeit expensive.” <strong>Persist</strong> means continue despite difficulty. <strong>Undergo</strong> means experience a process, often an unpleasant or formal one: the city will undergo major changes. Do not use undergo for something you actively do to someone else — that is usually <em>put through</em> or <em>subject to</em>.</p>`,
      points: [
        'Linkers on this list (albeit, nonetheless, notwithstanding, likewise, whereby) raise cohesion if used once, not in every sentence.',
        'Undergo + change/reform/surgery is a high-frequency academic collocation.',
        'Pose often means “present” a problem or question, not “sit for a photograph.”',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['adjacent', 'Next to or adjoining something else', 'The new clinic is adjacent to the existing hospital.'],
          ['albeit', 'Although (used before an adjective or phrase)', 'The scheme was successful, albeit expensive to run.'],
          ['compile', 'To assemble information from various sources', 'The team compiled a database of climate records.'],
          ['conceive', 'To form an idea; to imagine', 'It is hard to conceive of a city with no public transport.'],
          ['encounter', 'To meet or experience, often unexpectedly', 'Rural patients still encounter long delays.'],
          ['intrinsic', 'Belonging naturally; essential to the nature of something', 'Curiosity is an intrinsic motive for learning.'],
          ['nonetheless', 'In spite of that; however', 'The sample was small; nonetheless, the pattern was clear.'],
          ['notwithstanding', 'In spite of', 'Notwithstanding the cost, parliament approved the levy.'],
          ['persist', 'To continue to exist or to do something despite difficulty', 'If inequalities persist, growth alone will not convince voters.'],
          ['pose', 'To present a problem, danger, or question', 'Driverless cars pose difficult legal questions.'],
          ['undergo', 'To experience a process or change', 'The city will undergo major changes over the next decade.'],
          ['whereby', 'By which; according to which', 'They proposed a system whereby residents vote on local budgets.'],
        ],
      },
    },
    {
      title: 'Word families and grammar notes',
      content: `<p>Several Sublist 10 items barely change form, so the grammar around them matters more than suffixes. <em>Albeit</em> is not followed by a full finite clause in careful style: “albeit it was expensive” is clumsy; “albeit expensive” is clean. <em>Nonetheless</em> is a sentence adverb, like however. <em>Notwithstanding</em> can come before a noun phrase: “notwithstanding the delays.”</p>
<p>Verbs still have families. <em>Compile</em> → <em>compilation</em>. <em>Conceive</em> → <em>conception</em> / <em>conceivable</em>. <em>Convince</em> → <em>conviction</em> (also “a strongly held belief”). <em>Persist</em> → <em>persistence</em> / <em>persistent</em>. <em>Undergo</em> is irregular: undergo / underwent / undergone. The stub question uses the base form after “will.”</p>`,
      points: [
        'Will / should / must + undergo (base form), not undergoing or underwent.',
        'Reluctant is the adjective; reluctance is the noun: “reluctance to pay.”',
        'So-called warns that a label may be misleading: “the so-called reform.”',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['compile', 'compilation', 'compile', 'compiled'],
          ['conceive', 'conception', 'conceive', 'conceivable, inconceivable'],
          ['convince', 'conviction', 'convince', 'convincing, convincingly'],
          ['encounter', 'encounter', 'encounter', '—'],
          ['intrinsic', '—', '—', 'intrinsic, intrinsically'],
          ['persist', 'persistence', 'persist', 'persistent, persistently'],
          ['pose', 'pose', 'pose', '—'],
          ['reluctance', 'reluctance', '—', 'reluctant, reluctantly'],
          ['undergo', '—', 'undergo', '—'],
          ['integrity', 'integrity', '—', '—'],
          ['depress', 'depression', 'depress', 'depressing, depressed'],
          ['assemble', 'assembly', 'assemble', 'assembled'],
        ],
      },
    },
    {
      title: 'Common collocations',
      content: `<p>Because this list is short, you can learn almost every natural partnership. “Undergo change,” “persist over time,” “pose a threat,” “compile a report,” “intrinsic value,” “reluctance to,” and “a system whereby” will carry you through most exam uses.</p>
<p>Place and process: <em>adjacent to</em>, <em>assemble a team</em>, <em>forthcoming report</em>, <em>an ongoing investigation</em>, <em>a panel of experts</em>. Concession: <em>albeit limited</em>, <em>nonetheless</em>, <em>notwithstanding the risk</em>. Evaluation: <em>enormous pressure</em>, <em>straightforward process</em>, <em>odd result</em>, <em>invoke a law</em>, <em>levy a tax</em>.</p>`,
      points: [
        'Undergo collocates with change, reform, surgery, training, and review.',
        'Persist collocates with problems, inequalities, symptoms, and “persist in -ing.”',
      ],
      examples: [
        { label: 'undergo', text: 'undergo major changes, undergo reform, undergo training, undergone surgery' },
        { label: 'persist', text: 'persist over time, persist in, persistent inequality' },
        { label: 'albeit', text: 'successful, albeit expensive; useful, albeit limited' },
        { label: 'pose', text: 'pose a threat, pose a question, pose a challenge' },
        { label: 'compile', text: 'compile a report, compile data, compile a list' },
        { label: 'intrinsic', text: 'intrinsic value, intrinsic motivation, intrinsically linked' },
        { label: 'nonetheless', text: 'small; nonetheless useful / nonetheless, the risk remains' },
        { label: 'whereby', text: 'a system whereby, a process whereby, a deal whereby' },
        { label: 'encounter', text: 'encounter a problem, encounter resistance, first encounter' },
        { label: 'reluctance', text: 'reluctance to (do), show reluctance, growing reluctance' },
      ],
    },
    {
      title: 'Using Sublist 10 in IELTS writing',
      content: `<p>Use at most one fancy linker per paragraph. A clean pattern is concession then result: “The trial was small, albeit carefully designed; nonetheless, hospitals should undergo the same review.” That sentence uses three list items without looking like a catalogue. If you also drop <em>notwithstanding</em> and <em>whereby</em> into the same paragraph, the style becomes heavy.</p>
<p>In Reading, long sentences often hide the verb after a linker: “Notwithstanding earlier delays, the panel compiled…” Train yourself to find the main verb. In speaking, prefer <em>although</em> and <em>however</em> unless the clause is short enough for <em>albeit</em>. Spoken “albeit” is fine once; a chain of them sounds like a textbook.</p>`,
      points: [
        'Albeit + adjective/phrase; although + full clause.',
        'Nonetheless and nevertheless (Sublist 6) are near-synonyms; pick one and stay consistent.',
        'Intrinsic is close to inherent (Sublist 9): both mean built-in. Intrinsic often pairs with value or motivation.',
      ],
      examples: [
        { label: 'Weak sentence', text: 'The city will have major changes over the next decade.' },
        { label: 'Strong sentence', text: 'The city will undergo major changes over the next decade.' },
        { label: 'Weak sentence', text: 'The plan worked but it cost a lot.' },
        { label: 'Strong sentence', text: 'The plan worked, albeit at a high cost.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match word to definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        {
          text: 'What does “undergo” mean?',
          options: ['To experience a process or change', 'To cancel a process', 'To measure a process in grams'],
          answer: 'To experience a process or change',
          explanation: 'Undergo means go through something, often formal or difficult — reform, surgery, training. It is not a measurement verb.',
        },
        {
          text: 'What does “albeit” mean?',
          options: ['Although (before a phrase or adjective)', 'Because of that', 'In the same way'],
          answer: 'Although (before a phrase or adjective)',
          explanation: 'Albeit is a concise concession: “useful, albeit limited.” Likewise would mean “in the same way.”',
        },
        {
          text: 'What does “persist” mean?',
          options: ['To continue despite difficulty', 'To finish immediately', 'To translate a document'],
          answer: 'To continue despite difficulty',
          explanation: 'Problems persist when they do not go away. Persist can also mean keep doing something, as in “persist in asking.”',
        },
        {
          text: 'What does “intrinsic” mean?',
          options: ['Belonging naturally; essential to the nature of something', 'Borrowed from another field', 'Temporary and optional'],
          answer: 'Belonging naturally; essential to the nature of something',
          explanation: 'Intrinsic motivation comes from the activity itself. It is the opposite of a purely external incentive.',
        },
        {
          text: 'What does “whereby” mean?',
          options: ['By which; according to which', 'Next to something', 'Against someone’s will'],
          answer: 'By which; according to which',
          explanation: 'Whereby introduces the method in a system: “a scheme whereby residents vote.” Adjacent would mean “next to.”',
        },
      ],
    },
    {
      title: 'Complete the sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct Sublist 10 word.',
      questions: [
        {
          text: 'The city will ____ major changes over the next decade.',
          answer: 'undergo',
          explanation: 'After “will” the verb is in base form. Undergo collocates with changes, reform, and similar processes.',
        },
        {
          text: 'The trial succeeded, ____ with a very small sample.',
          answer: 'albeit',
          explanation: 'Albeit introduces a concession phrase. Here it means “although it had only a very small sample.”',
        },
        {
          text: 'If delays ____, patients will seek care in another region.',
          answer: 'persist',
          explanation: 'Persist means continue. After “if” and a plural subject, the present simple persist is correct.',
        },
        {
          text: 'Self-driving cars ____ serious questions about liability.',
          answer: 'pose',
          explanation: 'Pose a question / pose a problem is the academic collocation. After a plural subject, use pose.',
        },
      ],
    },
    {
      title: 'Choose the correct word form',
      type: 'select',
      instructions: 'Select the grammatically correct form for each sentence.',
      questions: [
        {
          text: 'The city will ____ major changes over the next decade. (undergo)',
          options: ['undergo', 'undergoing', 'underwent'],
          answer: 'undergo',
          explanation: '“Will” must be followed by the base verb. Undergoing is a participle; underwent is past simple.',
        },
        {
          text: 'Staff showed some ____ to adopt the new software. (reluctant)',
          options: ['reluctance', 'reluctant', 'reluctantly'],
          answer: 'reluctance',
          explanation: 'After “some” you need a noun. Reluctance to + verb is the standard pattern.',
        },
        {
          text: 'The researchers ____ a list of every incident in the decade. (compile)',
          options: ['compiled', 'compilation', 'compiler'],
          answer: 'compiled',
          explanation: 'The sentence needs a past verb. Compilation is a noun; compiler is a person or a program.',
        },
        {
          text: 'Learning can be ____ motivating when students choose the topic. (intrinsic)',
          options: ['intrinsic', 'intrinsically', 'intrinsics'],
          answer: 'intrinsically',
          explanation: 'You need an adverb to modify “motivating.” Intrinsically motivating is a standard education collocation.',
        },
      ],
    },
  ],
  tips: [
    'Albeit takes a phrase (“albeit costly”), not a full “it was” clause in careful style.',
    'Undergo is irregular: undergo / underwent / undergone — lock the “will undergo” pattern.',
    'Use nonetheless or notwithstanding once per essay; stacking both looks forced.',
    'Pose a problem/question/threat — do not write “pose a happy result.”',
  ],
  dailyPractice: [
    'Write one concession sentence with albeit and one with nonetheless — keep them in different paragraphs.',
    'Rewrite “the city will have big changes” with undergo and one extra Sublist 10 word.',
    'Scan a broadsheet editorial for whereby, notwithstanding, or persist.',
    'Drill the irregular forms of undergo aloud, then use undergone in a present-perfect sentence.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 10 vocabulary course covering Coxhead headwords such as adjacent, albeit, compile, conceive, encounter, intrinsic, nonetheless, notwithstanding, persist, pose, undergo, and whereby. Includes the full stub list, grammar notes for linkers, collocations, and IELTS uses for concession and process.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist10Data,
      title: awlSublist10Data.title + ' (Foundations)',
      overview: { ...awlSublist10Data.overview, duration: '15-20 minutes' },
      sections: awlSublist10Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist10Data.sections.length / 2))),
      exercises: awlSublist10Data.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist10Data,
      title: awlSublist10Data.title + ' (Advanced)',
      overview: { ...awlSublist10Data.overview, duration: '30-35 minutes' },
      sections: awlSublist10Data.sections,
      exercises: awlSublist10Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist10Data.tips || []),
        'For Band 8+: use whereby to define a mechanism in one clause instead of a new sentence.',
        'Advanced tip: pair persist with a concrete subject (delays, gaps, symptoms), not a vague “problems.”',
      ],
    };
  }
  return awlSublist10Data;
}
